import { useEffect, useState, useMemo } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { Download, FileIcon, FileText } from "lucide-react";
import { supabase } from "@/lib/supabase";
import { VendorPdfPreview } from "./vendor-pdf-preview.component";
import { useStorage } from "../../hooks/useStorage";
import { Loading } from "@/components/src/animations/loading";
import { format } from "date-fns";
import { VendorSharedInvoice } from "@/lib/types/job-list.types";
import { getReminderStageLabel } from "@/lib/utils/reminder-stage-label";

const INVOICE_TYPE_LABELS: Record<number, string> = {
	0: "Rechnung",
	1: "Barrechnung",
	2: "Akontorechnung",
	11: "Kartenzahlung",
	20: "Sammelrechnung",
	98: "Rückvergütung",
	97: "Mahngebühr",
};

interface VendorInvoicesPageProps {
	organizationId: string;
}

export const VendorInvoicesPage: React.FC<VendorInvoicesPageProps> = ({
	organizationId,
}) => {
	const { vendorId } = useParams<{ vendorId: string }>();
	const [searchParams] = useSearchParams();
	const initialFileId = searchParams.get("file");

	const [invoices, setInvoices] = useState<VendorSharedInvoice[]>([]);
	const [loading, setLoading] = useState(true);
	const [selectedFileId, setSelectedFileId] = useState<string | null>(null);
	const [selectedFileUrl, setSelectedFileUrl] = useState<string | null>(null);

	const { fetchFileFromPath, downloadFromSupabase } = useStorage();

	useEffect(() => {
		const fetchInvoices = async () => {
			if (!vendorId || !organizationId) return;

			setLoading(true);
			const { data, error } = await supabase.rpc(
				"get_vendor_shared_invoices" as never,
				{
					p_vendor_org_id: vendorId,
					p_customer_org_id: organizationId,
				} as never
			);

			if (error || !data) {
				setInvoices([]);
				setLoading(false);
				return;
			}

			setInvoices(data as unknown as VendorSharedInvoice[]);
			setLoading(false);
		};

		fetchInvoices();
	}, [vendorId, organizationId]);

	useEffect(() => {
		if (invoices.length === 0) return;

		if (initialFileId && initialFileId !== selectedFileId) {
			const target = invoices.find((i) => i.file_id === initialFileId);
			if (target) {
				setSelectedFileId(initialFileId);
				return;
			}
		}

		if (!selectedFileId) {
			setSelectedFileId(invoices[0].file_id);
		}
	}, [invoices, selectedFileId, initialFileId]);

	const selectedInvoice = useMemo(
		() => invoices.find((i) => i.file_id === selectedFileId),
		[invoices, selectedFileId]
	);

	useEffect(() => {
		const loadUrl = async () => {
			if (!selectedInvoice) {
				setSelectedFileUrl(null);
				return;
			}
			const result = await fetchFileFromPath(
				selectedInvoice.file_path_name
			);
			setSelectedFileUrl(result?.data ?? null);
		};
		loadUrl();
	}, [selectedInvoice?.file_id]);

	const handleDownload = () => {
		if (selectedInvoice?.file_path_name) {
			downloadFromSupabase(selectedInvoice.file_path_name);
		}
	};

	if (loading) {
		return <Loading />;
	}

	if (invoices.length === 0) {
		return (
			<div className="flex flex-col items-center justify-center h-full gap-2">
				<FileText size={32} className="text-stone-300" />
				<span className="text-sm text-stone-400">
					Keine geteilten Rechnungen vorhanden
				</span>
			</div>
		);
	}

	return (
		<div className="h-full flex">
			<div className="w-[350px] h-full border-r border-stone-200 flex flex-col">
				<div className="px-4 py-2 border-b border-stone-200 flex-shrink-0">
					<div className="font-semibold text-stone-800">
						Rechnungen
					</div>
				</div>
				<div className="flex-1 overflow-y-auto">
					{invoices.map((inv) => (
						<button
							key={inv.file_id}
							onClick={() => setSelectedFileId(inv.file_id)}
							className={`w-full text-left px-4 py-3 border-b border-stone-100 transition-colors ${
								selectedFileId === inv.file_id
									? "bg-stone-100"
									: "hover:bg-stone-50"
							}`}
							aria-label={
								inv.is_reminder
									? `${getReminderStageLabel(inv.reminder_stage ?? 0)} zu Rechnung ${inv.invoice_doc_number}`
									: `Rechnung ${inv.invoice_doc_number}`
							}
							data-testid={`vendor-invoice-item-${inv.file_id}`}
						>
							<div className="flex items-center justify-between">
								<span className="text-sm font-medium text-stone-800">
									{inv.is_reminder
										? `${getReminderStageLabel(inv.reminder_stage ?? 0)} zu Rg. ${inv.invoice_doc_number}`
										: inv.invoice_doc_number || "–"}
								</span>
								<span className="text-xs text-stone-400">
									{inv.invoice_date
										? format(
												new Date(inv.invoice_date),
												"dd.MM.yy"
											)
										: "–"}
								</span>
							</div>
							<span className="flex items-center gap-2 text-xs text-stone-500">
								{inv.is_reminder
									? "Mahnung"
									: INVOICE_TYPE_LABELS[inv.invoice_type] ??
										"Rechnung"}
								{!inv.is_reminder && !!inv.reminder_stage && (
									<span
										className="rounded bg-amber-50 px-1.5 font-medium text-amber-700"
										data-testid={`vendor-invoice-reminder-stage-${inv.file_id}`}
									>
										{getReminderStageLabel(inv.reminder_stage)}
									</span>
								)}
							</span>
						</button>
					))}
				</div>
			</div>

			<div className="flex-1 min-w-0 h-full flex flex-col bg-stone-200/60">
				{selectedInvoice && (
					<div className="flex items-center justify-between px-4 py-2 border-b border-stone-200 flex-shrink-0">
						<span className="text-sm text-stone-600 truncate">
							{selectedInvoice.file_name}
						</span>
						<button
							onClick={handleDownload}
							className="flex items-center gap-1.5 text-sm text-stone-500 hover:text-stone-800 transition-colors"
							aria-label="Rechnung herunterladen"
							data-testid="vendor-invoice-download"
						>
							<Download size={14} />
							<span>Herunterladen</span>
						</button>
					</div>
				)}
				<div className="flex-1 min-h-0">
					{selectedFileUrl ? (
						<VendorPdfPreview url={selectedFileUrl} />
					) : (
						<div className="flex flex-col items-center justify-center h-full gap-2">
							<FileIcon size={32} className="text-stone-300" />
							<span className="text-sm text-stone-400">
								Wählen Sie eine Rechnung aus
							</span>
						</div>
					)}
				</div>
			</div>
		</div>
	);
};
